import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*

/**
 * Veřejná část turnajů (spec 17.9) — běží na subdoméně `turnaje.<doména>`, ale je to tentýž build
 * i tytéž komponenty jako liga (viz `SiteMode` v Main.kt).
 *
 * Koncepty (`status = draft`) vůbec nevrací RLS, takže se tady nefiltrují — přihlášenému
 * administrátorovi se ale vrátí, a proto se u nich zobrazuje odznak „Koncept" (spec 17.11).
 */
@Composable
fun PublicTournamentsScreen(
    token: String?,
    season: SeasonDto?,
    onOpen: (TournamentDto) -> Unit,
) {
    var tournaments by remember(season?.id) { mutableStateOf<List<TournamentDto>>(emptyList()) }
    var loading by remember(season?.id) { mutableStateOf(true) }
    var error by remember(season?.id) { mutableStateOf<String?>(null) }

    LaunchedEffect(season?.id, token) {
        val sid = season?.id
        if (sid == null) {
            tournaments = emptyList()
            loading = false
            return@LaunchedEffect
        }
        loading = true
        try {
            tournaments = Api.fetchTournaments(token, sid)
            error = null
        } catch (e: Throwable) {
            error = "Načtení turnajů selhalo: ${e.apiMessage()}"
        }
        loading = false
    }

    if (loading || error != null || tournaments.isEmpty()) {
        Div(attrs = { classes("card") }) {
            if (loading) Text("Načítám…")
            error?.let { Div(attrs = { classes("editor-error") }) { Text("Chyba: $it") } }
            if (!loading && error == null && tournaments.isEmpty()) {
                Text(if (season == null) "Bez aktivní sezóny." else "V této sezóně zatím nejsou žádné turnaje.")
            }
        }
    }

    if (tournaments.isNotEmpty()) {
        Div(attrs = { classes("cards-grid") }) {
            tournaments.forEach { t ->
                Div(attrs = { classes("card", "clickable-row", "tournament-card"); onClick { onOpen(t) } }) {
                    H3 { Text(t.title) }
                    P(attrs = { classes("muted") }) {
                        Text(formatTournamentDateRange(t.startsAt, t.endsAt) + " · " + tournamentTypeLabel(t.type))
                    }
                    Div {
                        Span(attrs = { classes("tag") }) { Text(tournamentStatusLabel(t.status)) }
                        if (t.isDraft) {
                            Text(" ")
                            Span(attrs = { classes("tag") }) { Text("Koncept — nepublikováno") }
                        }
                    }
                }
            }
        }
    }
}

enum class PublicTournamentTab(val label: String, val path: String) {
    DESCRIPTION("Popis", ""),
    SLOTS("Obsazovačka", "obsazovacka"),
    GROUPS("Rozdělení do skupin", "skupiny"),
    RESULTS("Výsledky", "vysledky"),
}

/** Detail turnaje: popis, obsazovačka a výsledky (obsah podle typu, spec 17.9). */
@Composable
fun PublicTournamentScreen(
    token: String?, season: SeasonDto?, slug: String,
    tab: PublicTournamentTab,
    onTabChange: (PublicTournamentTab) -> Unit,
    onTitleResolved: (String?) -> Unit,
) {
    var tournament by remember(slug, season?.id) { mutableStateOf<TournamentDto?>(null) }
    var entries by remember(slug, season?.id) { mutableStateOf<List<TournamentEntryDto>>(emptyList()) }
    var slots by remember(slug, season?.id) { mutableStateOf<List<TournamentSlotDto>>(emptyList()) }
    var grouping by remember(slug, season?.id) { mutableStateOf<TournamentGroupingDto?>(null) }
    var groupPlayers by remember(slug, season?.id) { mutableStateOf<List<TournamentGroupPlayerDto>>(emptyList()) }
    var scores by remember(slug, season?.id) { mutableStateOf<List<TournamentScoreDto>>(emptyList()) }
    var teamByPlayer by remember(slug, season?.id) { mutableStateOf<Map<String, String>>(emptyMap()) }
    var loading by remember(slug, season?.id) { mutableStateOf(true) }
    var error by remember(slug, season?.id) { mutableStateOf<String?>(null) }

    LaunchedEffect(slug, season?.id, token) {
        val sid = season?.id
        if (sid == null) {
            loading = false
            return@LaunchedEffect
        }
        loading = true
        try {
            val t = Api.fetchTournamentBySlug(token, sid, slug)
            tournament = t
            onTitleResolved(t?.title)
            if (t != null) {
                entries = Api.fetchTournamentEntries(token, t.id)
                slots = Api.fetchTournamentSlots(token, t.id)
                grouping = Api.fetchTournamentGrouping(token, t.id)
                groupPlayers = Api.fetchTournamentGroupPlayers(token, t.id)
                scores = Api.fetchTournamentScores(token, t.id)
                val roster = Api.fetchRosterForSeason(token, sid)
                val seasonTeams = Api.fetchAllTeamsForSeason(token, sid)
                val teamNameById = seasonTeams.associate { slt ->
                    slt.teamId to (slt.teamNameOverride ?: slt.team?.name ?: "")
                }
                teamByPlayer = roster.associate { it.playerId to (teamNameById[it.teamId] ?: "") }
            }
            error = null
        } catch (e: Throwable) {
            error = "Načtení turnaje selhalo: ${e.apiMessage()}"
        }
        loading = false
    }

    val t = tournament
    val groupsVisible = t?.config?.showGroups == true
    val visibleTab = if (tab == PublicTournamentTab.GROUPS && !groupsVisible) {
        PublicTournamentTab.DESCRIPTION
    } else tab
    Div(attrs = { classes("card", "tournament-header") }) {
        if (loading) Text("Načítám…")
        error?.let { Div(attrs = { classes("editor-error") }) { Text("Chyba: $it") } }
        if (t == null) {
            if (!loading && error == null) Text("Turnaj nenalezen.")
            return@Div
        }
        P(attrs = { classes("muted") }) {
            Text(formatTournamentDateRange(t.startsAt, t.endsAt) + " · " + tournamentTypeLabel(t.type) + " · " + tournamentStatusLabel(t.status))
        }
        if (t.isDraft) {
            P(attrs = { classes("muted") }) { Text("Koncept — tenhle turnaj zatím veřejně vidět není.") }
        }
        Div(attrs = { classes("actions", "tournament-tabs") }) {
            PublicTournamentTab.entries.forEach { candidate ->
                if (candidate != PublicTournamentTab.GROUPS || groupsVisible) {
                    NavButton(candidate.label, visibleTab == candidate) { onTabChange(candidate) }
                }
            }
        }
    }

    if (t == null) return

    when (visibleTab) {
        PublicTournamentTab.DESCRIPTION -> Div(attrs = { classes("card") }) {
            val html = t.bodyHtml
            if (html.isNullOrBlank()) Text("Popis turnaje zatím není vyplněný.") else RichContent(html)
        }
        PublicTournamentTab.SLOTS -> TournamentSlotsPublic(slots = slots, entries = entries, teamByPlayer = teamByPlayer)
        PublicTournamentTab.GROUPS -> Div(attrs = { classes("card") }) {
            H2 { Text("Rozdělení do skupin") }
            TournamentGroupsOverview(grouping, groupPlayers)
        }
        PublicTournamentTab.RESULTS -> TournamentResultsPublic(
            tournament = t, entries = entries, scores = scores, teamByPlayer = teamByPlayer,
        )
    }
}

@Composable
private fun TournamentSlotsPublic(
    slots: List<TournamentSlotDto>,
    entries: List<TournamentEntryDto>,
    teamByPlayer: Map<String, String>,
) {
    val bySlot = entries.groupBy { it.slotId }
    Div(attrs = { classes("card") }) {
        H2 { Text("Obsazovačka") }
        if (slots.isEmpty()) {
            Text("Termíny zatím nejsou vypsané.")
            return@Div
        }
        groupedTournamentSlots(slots).forEach { (day, daySlots) ->
            Div(attrs = { classes("tournament-day") }) {
                H4 { Text(formatDate(day.date)) }
                if (day.description.isNotBlank()) P { Text(day.description) }
                daySlots.forEach { slot ->
                    val assigned = bySlot[slot.id].orEmpty().sortedBy { it.playerName.lowercase() }
                    Div(attrs = { classes("roster-tile", "tournament-slot") }) {
                        Div {
                            B { Text(slotTimeLabel(slot)) }
                            slot.label?.takeIf { it.isNotBlank() }?.let { Text(" · $it") }
                            Text(" · ")
                            Span(attrs = { classes("tag") }) { Text("${assigned.size}/${slot.capacity}") }
                        }
                        if (assigned.isEmpty()) {
                            P(attrs = { classes("muted") }) { Text("Zatím volno.") }
                        } else {
                            Ol {
                                assigned.forEach { entry ->
                                    Li { Text("${entry.playerName} — ${teamOrGuestLabel(entry.playerId, teamByPlayer)}") }
                                }
                            }
                        }
                    }
                }
            }
        }
        val withoutSlot = bySlot[null].orEmpty()
        if (withoutSlot.isNotEmpty()) {
            H4 { Text("Bez termínu") }
            Ul {
                withoutSlot.sortedBy { it.playerName.lowercase() }.forEach { entry ->
                    Li { Text("${entry.playerName} — ${teamOrGuestLabel(entry.playerId, teamByPlayer)}") }
                }
            }
        }
    }
}

@Composable
private fun TournamentResultsPublic(
    tournament: TournamentDto,
    entries: List<TournamentEntryDto>,
    scores: List<TournamentScoreDto>,
    teamByPlayer: Map<String, String>,
) {
    val games = tournament.gamesPerPlayer
    val rows = buildTournamentRows(games, entries, scores).filter { it.hasAnyScore }

    when (tournament.type) {
        TOURNAMENT_BRACKET -> {
            val (men, women) = splitByGender(rows)
            Div(attrs = { classes("card") }) {
                H2 { Text("Kvalifikace — muži") }
                ResultTable(rankByTotal(men), games, teamByPlayer, tournament.config.poMen ?: 24)
            }
            Div(attrs = { classes("card") }) {
                H2 { Text("Kvalifikace — ženy") }
                ResultTable(rankByTotal(women), games, teamByPlayer, tournament.config.poWomen ?: 16)
            }
            val unknown = withoutGender(rows)
            if (unknown.isNotEmpty()) {
                Div(attrs = { classes("card") }) {
                    H2 { Text("Bez zařazení") }
                    P(attrs = { classes("muted") }) { Text("Účastníci bez vyplněného pohlaví.") }
                    ResultTable(rankByTotal(unknown), games, teamByPlayer, null)
                }
            }
            Div(attrs = { classes("card") }) {
                P(attrs = { classes("muted") }) { Text("Play-off se zveřejní po dohrání kvalifikace.") }
            }
        }
        TOURNAMENT_BONUS -> {
            val pct = tournament.config.bonusPct ?: 50
            listOf("A", "B").forEach { group ->
                val groupRows = rows.filter { it.entry.groupCode == group }
                Div(attrs = { classes("card") }) {
                    H2 { Text("Skupina $group") }
                    if (groupRows.isEmpty()) Text("Zatím bez výsledků.")
                    else BonusResultTable(rankByBonusTotal(groupRows, pct), games, teamByPlayer, pct)
                }
            }
            val ungrouped = rows.filter { it.entry.groupCode == null }
            if (ungrouped.isNotEmpty()) {
                Div(attrs = { classes("card") }) {
                    H2 { Text("Bez skupiny") }
                    BonusResultTable(rankByBonusTotal(ungrouped, pct), games, teamByPlayer, pct)
                }
            }
        }
        else -> Div(attrs = { classes("card") }) {
            H2 { Text("Výsledky") }
            ResultTable(rankByTotal(rows), games, teamByPlayer, null)
        }
    }
}

/**
 * Tabulka pořadí. [advancing] = kolik hráčů postupuje do play-off — hranice se vykreslí jako
 * oddělovač, ať je na první pohled vidět, kdo je nad čarou.
 */
@Composable
private fun ResultTable(
    rows: List<TournamentResultRow>,
    games: Int,
    teamByPlayer: Map<String, String>,
    advancing: Int?,
) {
    if (rows.isEmpty()) {
        Text("Zatím bez výsledků.")
        return
    }
    val unresolved = if (advancing != null) unresolvedQualificationIds(rows) else emptySet()
    val cutoffNeedsDraw = advancing != null && rows.getOrNull(advancing - 1)?.entry?.id in unresolved &&
        rows.getOrNull(advancing)?.entry?.id in unresolved
    if (unresolved.isNotEmpty()) P(attrs = { classes("muted") }) {
        Text("U shodných výkonů označených „los“ určí pořadí administrátor podle losu.")
    }
    Div(attrs = { classes("table-scroll") }) {
        Table(attrs = { classes("public-table") }) {
            Tr {
                Th { Text("#") }
                Th { Text("Hráč") }
                Th { Text("Tým / host") }
                for (i in 1..games) Th { Text("Hra $i") }
                Th(attrs = { classes("col-accent") }) { Text("Součet") }
            }
            rows.forEachIndexed { index, row ->
                val rank = index + 1
                Tr(attrs = {
                    if (advancing != null && !cutoffNeedsDraw && rank == advancing && rows.size > advancing) classes("group-boundary")
                }) {
                    Td {
                        if (row.entry.id in unresolved) Text("los")
                        else if (row.hasAnyScore) RankBadge(rank, prizePlaces = 5)
                        else Text("—")
                    }
                    Td { Text(row.entry.playerName) }
                    Td { Text(teamOrGuestLabel(row.entry.playerId, teamByPlayer)) }
                    row.scores.forEach { score -> Td { Text(score?.toString() ?: "—") } }
                    Td { Span(attrs = { classes("pill") }) { Text(if (row.hasAnyScore) row.total.toString() else "—") } }
                }
            }
        }
    }
}

/** Bonifikační tabulka — navíc sloupce s označenou hrou, bonusem a součtem s bonusem (spec 17.6). */
@Composable
private fun BonusResultTable(
    rows: List<TournamentResultRow>,
    games: Int,
    teamByPlayer: Map<String, String>,
    bonusPct: Int,
) {
    Div(attrs = { classes("table-scroll") }) {
        Table(attrs = { classes("public-table") }) {
            Tr {
                Th { Text("#") }
                Th { Text("Hráč") }
                Th { Text("Tým / host") }
                for (i in 1..games) Th { Text("Hra $i") }
                Th { Text("Součet") }
                Th { Text("Bonus") }
                Th(attrs = { classes("col-accent") }) { Text("Celkem") }
            }
            rows.forEachIndexed { index, row ->
                Tr {
                    Td { if (row.hasAnyScore) RankBadge(index + 1, prizePlaces = 5) else Text("—") }
                    Td { Text(row.entry.playerName) }
                    Td { Text(teamOrGuestLabel(row.entry.playerId, teamByPlayer)) }
                    row.scores.forEachIndexed { idx, score ->
                        val isBonusCell = row.bonusGameNo == idx + 1
                        Td {
                            Span(attrs = {
                                if (isBonusCell) {
                                    classes("tournament-bonus-score")
                                    attr("title", "Bonifikační hra")
                                }
                            }) { Text(score?.toString() ?: "—") }
                        }
                    }
                    Td { Text(if (row.hasAnyScore) row.total.toString() else "—") }
                    Td { Text(if (row.bonusGameNo != null) "+${row.bonusPoints(bonusPct)}" else "—") }
                    Td {
                        Span(attrs = { classes("pill") }) {
                            Text(if (row.hasAnyScore) row.totalWithBonus(bonusPct).toString() else "—")
                        }
                    }
                }
            }
        }
    }
}
